import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import moment from 'moment';

import { DateFormat } from '../../../../../../../utils/date';

import {
  ProgramInfoContainer,
  ProgramItem,
  ProgramName,
  ProgramDateRange,
} from './styles';

const ProgramInfo = ({
  workoutAssignments,
  coachPrograms,
  visibleDateRange,
}) => {
  const programAssignments = useMemo(() => {
    const docs = workoutAssignments?.docs ? [...workoutAssignments.docs] : [];
    if (!docs.length || !coachPrograms || !visibleDateRange) {
      return [];
    }

    // Create a map of programId to program name
    const programMap = new Map();
    coachPrograms.forEach((program) => {
      programMap.set(program.id, program.name);
    });

    // Group workout assignments by programId
    const assignmentsByProgram = {};
    docs.forEach((assignment) => {
      if (assignment.programId) {
        if (!assignmentsByProgram[assignment.programId]) {
          assignmentsByProgram[assignment.programId] = [];
        }
        assignmentsByProgram[assignment.programId].push(assignment);
      }
    });

    // Calculate date ranges for each program within visible dates
    const visibleStart = moment(visibleDateRange.start);
    const visibleEnd = moment(visibleDateRange.end);

    const programInfoList = [];

    Object.entries(assignmentsByProgram).forEach(([programId, assignments]) => {
      const programName = programMap.get(programId);
      if (!programName) return;

      // Get all dates for this program and filter dates that are within visible range
      const visibleDates = assignments
        .map((assignment) => assignment.momentStartDate.clone())
        .filter((date) => (
          date.isSameOrAfter(visibleStart, 'day') && date.isBefore(visibleEnd, 'day')
        ));

      if (visibleDates.length === 0) return;

      // Find min and max dates within visible range
      const minDate = moment.min(visibleDates);
      const maxDate = moment.max(visibleDates);

      // Format date range
      let dateRangeText = '';
      if (minDate.isSame(maxDate, 'day')) {
        dateRangeText = minDate.format(DateFormat.DATE_MONTH_SHORT_DISPLAY_FORMAT);
      } else {
        dateRangeText = `${minDate.format(DateFormat.DATE_MONTH_SHORT_DISPLAY_FORMAT)}
          - ${maxDate.format(DateFormat.DATE_MONTH_SHORT_DISPLAY_FORMAT)}`;
      }

      programInfoList.push({
        programId,
        programName,
        dateRange: dateRangeText,
        minDate,
        maxDate,
      });
    });

    // Sort by start date
    return programInfoList.slice().sort((a, b) => a.minDate.valueOf() - b.minDate.valueOf());
  }, [workoutAssignments, coachPrograms, visibleDateRange]);

  if (programAssignments.length === 0) {
    return null;
  }

  return (
    <ProgramInfoContainer>
      {programAssignments.map(({ programId, programName, dateRange }) => (
        <ProgramItem key={programId}>
          <ProgramName>{programName}</ProgramName>
          <ProgramDateRange>{dateRange}</ProgramDateRange>
        </ProgramItem>
      ))}
    </ProgramInfoContainer>
  );
};

ProgramInfo.propTypes = {
  workoutAssignments: PropTypes.shape({
    docs: PropTypes.arrayOf(PropTypes.object),
  }),
  coachPrograms: PropTypes.arrayOf(PropTypes.object),
  visibleDateRange: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    end: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  }),
};

ProgramInfo.defaultProps = {
  workoutAssignments: { docs: [] },
  coachPrograms: [],
  visibleDateRange: null,
};

export default observer(ProgramInfo);
